import React, { useState, useEffect } from 'react';

import { http } from '../../lib/http';

const ExClientsTab = ({ setMessage, setError }) => {

    const [exClients, setExClients] = useState([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 100,
        total_count: 0
    });

    const loadExClients = async (page = 1, limit = 100, append = false) => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const params = new URLSearchParams({
                page: page.toString(),
                limit: limit.toString()
            });

            const response = await http.get(
                `/admin/ex-clients?${params}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const payload = response.data.data || response.data;
            if (append) {
                // Append new ex-clients to existing list (for Load More)
                setExClients(prev => [...prev, ...(payload.ex_clients || [])]);
            } else {
                // Replace ex-clients list (for initial load/refresh)
                setExClients(payload.ex_clients || []);
            }

            setPagination(payload.pagination || pagination);

        } catch (error) {
            console.error('Failed to load ex-clients:', error);
            setError(error.response?.data?.detail || 'Failed to load ex-clients');
        } finally {
            setLoading(false);
        }
    };

    const loadMoreExClients = async () => {
        const nextPage = pagination.page + 1;
        await loadExClients(nextPage, 100, true); // append = true
    };



    const deleteExClient = async (exClient) => {
        if (!confirm(`Permanently delete ${exClient.first_name} ${exClient.last_name}? This action cannot be undone.`)) {
            return;
        }

        try {
            await http.delete(`/admin/ex-clients/${exClient.id}`);

            setMessage(`🗑️ Deleted: ${exClient.first_name} ${exClient.last_name}`);
            loadExClients();
        } catch (error) {
            console.error('Failed to delete ex-client:', error);
            setError(error.response?.data?.detail || 'Failed to delete ex-client');
        }
    };

    useEffect(() => {
        loadExClients();
    }, []);

    if (loading) {
        return (
            <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-4"></div>
                <p className="text-gray-700">Loading ex-clients...</p>
            </div>
        );
    }

    if (exClients.length === 0) {
        return (
            <div className="text-center py-8">
                <h3 className="text-lg font-semibold text-white mb-2">No Ex-Clients</h3>
                <p className="text-gray-700">All customers are currently active</p>
                <button
                    onClick={() => loadExClients()}
                    className="mt-4 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md"
                >
                    Refresh
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-semibold text-white">
                        Ex-Clients ({exClients.length} of {pagination.total_count})
                    </h3>
                    <p className="text-white">Cancelled customers and deleted signups</p>
                </div>
                <button
                    onClick={() => loadExClients()}
                    className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-md"
                >
                    Refresh
                </button>
            </div>

            <div className="bg-gray-800 rounded-lg shadow-xl border-2 border-orange-500 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200" style={{ "minWidth": "3000px" }}>
                        <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 z-20 bg-gray-50">Name</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Email</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cancelled Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {exClients.map((client) => (
                                <tr key={client.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap sticky left-0 z-10 bg-white">
                                        <div className="text-sm font-medium text-gray-900">
                                            {client.first_name} {client.last_name}
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{client.email}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{client.cancellation_date || 'Unknown'}</div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="text-sm text-gray-900">{client.cancellation_reason || 'Unknown reason'}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <div className="flex gap-2">

                                            <button
                                                onClick={() => deleteExClient(client)}
                                                className="bg-orange-500 hover:bg-orange-500 text-white px-3 py-1 rounded-md text-xs"
                                            >
                                                🗑️ Delete
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Load More Button */}
            {pagination.total_count > exClients.length && (
                <div className="mt-4 text-center">
                    <button
                        onClick={loadMoreExClients}
                        disabled={loading}
                        className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-500 disabled:cursor-not-allowed text-white px-6 py-3 rounded-md font-medium transition-colors"
                    >
                        {loading ? (
                            <span className="flex items-center gap-2">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                                Loading...
                            </span>
                        ) : (
                            `Load More (${pagination.total_count - exClients.length} remaining)`
                        )}
                    </button>
                </div>
            )}

        </div>
    );
};

export default ExClientsTab;
